import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useUserPermissions } from './useUserPermissions';
import { ACTIVE_STAGES, CLOSED_STAGES } from '@/constants/dealStages';

const dealsDebugEnabled = import.meta.env.DEV;
function logDeals(...args: unknown[]) {
  if (dealsDebugEnabled) console.log(...args);
}
function errorDeals(...args: unknown[]) {
  if (dealsDebugEnabled) console.error(...args);
}

type Deal = Database['public']['Tables']['deals']['Row'];
type DealInsert = Database['public']['Tables']['deals']['Insert'];  
type DealUpdate = Database['public']['Tables']['deals']['Update'];

interface UseDealsOptions {
  stageFilter?: 'all' | 'active' | 'closed';
  page?: number;
  limit?: number;
  assignedTo?: string | string[]; // Support filtering by specific user(s)
  sdrIds?: string[];        // IDs dos SDRs ou ['unassigned']
  consultantIds?: string[]; // IDs dos Consultores ou ['unassigned']
  enabled?: boolean; // Control when query starts (for waiting on filter preferences)
  closingDateFrom?: string | null; // ISO date string (yyyy-MM-dd)
  closingDateTo?: string | null;   // ISO date string (yyyy-MM-dd)
  createdFrom?: string | null; // ISO timestamp string for created_at >= filter
  createdTo?: string | null;   // ISO timestamp string for created_at <= filter
  sourceFilter?: string | null; // deals_source filter value
  channelFilter?: string | null; // deals_channel filter value
  businessUnitFilter?: string | null; // business_unit filter value
}

// Optimized SELECT for list views - only fields needed for Kanban/Table display
const LIST_SELECT = `
  id,
  name,
  stage,
  total_amount,
  currency,
  created_at,
  sales_consultant,
  sales_development,
  contact_id,
  deals_source,
  deals_channel,
  business_unit,
  closed_date,
  stage_lost_at,
  contact:contacts!fk_deals_contact(
    id,
    first_name,
    last_name,
    primary_phone,
    primary_phone_country_code
  ),
  sold_by:app_users!sales_consultant(
    id,
    display_name
  ),
  sales_dev:app_users!sales_development(
    id,
    display_name
  ),
  deal_tags(
    id,
    tag_id,
    stage_tags(id, name, color)
  )
`;

// Use a flexible type for deals since we're using optimized SELECT
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DealListItem = any;

// Pagination constants
const CARDS_PER_STAGE = 25; // Cards per stage initially
const LOAD_MORE_INCREMENT = 25; // Load more per scroll

// Cache structure for storing deals by filter key
interface DealsCache {
  dealsByStage: Record<string, DealListItem[]>;
  stageCounts: Record<string, { count: number; amount: number }>;
  timestamp: number;
}

function normalizeFilterList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .slice()
    .sort();
}

export function useDeals(options: UseDealsOptions = {}) {
  const { stageFilter = 'active', page = 1, limit = 100, assignedTo, sdrIds, consultantIds, enabled = true, closingDateFrom, closingDateTo, createdFrom, createdTo, sourceFilter, channelFilter, businessUnitFilter } = options;
  
  // Per-stage state (new approach)
  const [dealsByStage, setDealsByStage] = useState<Record<string, DealListItem[]>>({});
  const [stageCounts, setStageCounts] = useState<Record<string, { count: number; amount: number }>>({});
  const [hasMoreByStage, setHasMoreByStage] = useState<Record<string, boolean>>({});
  const [loadingMoreStage, setLoadingMoreStage] = useState<string | null>(null);
  
  // Legacy flat array for compatibility
  const [deals, setDeals] = useState<DealListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [backgroundProgress, setBackgroundProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [stageServerCounts, setStageServerCounts] = useState<Record<string, number>>({});
  const { permissions, getScope, isLoading: permissionsLoading } = useUserPermissions();

  // Use stable references to prevent re-renders
  const permissionsId = permissions?.id;
  const scope = getScope('clients');
  
  // Track if we've already loaded to prevent duplicates
  const hasLoadedRef = useRef(false);
  const lastQueryKeyRef = useRef<string>('');
  
  // Debounce timer ref to prevent rapid-fire queries
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Loading lock to prevent concurrent requests
  const isLoadingRef = useRef(false);
  
  // Cache by filter key (persists across filter changes within same session)
  const dealsCacheRef = useRef<Record<string, DealsCache>>({});
  
  // Cache TTL in milliseconds (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;

  // Create a STABLE query key using useMemo to prevent unnecessary recalculations
  // Normalize assignedTo to a stable string representation
  const stableAssignedToKey = React.useMemo(() => {
    if (!assignedTo) return 'none';
    if (assignedTo === 'unassigned') return 'unassigned';
    if (Array.isArray(assignedTo)) return normalizeFilterList(assignedTo).join(',') || 'none';
    return assignedTo;
  }, [assignedTo]);

  const stableSdrKey = React.useMemo(() =>
    normalizeFilterList(sdrIds).join(',') || 'none', [sdrIds]);
  const stableConsultantKey = React.useMemo(() =>
    normalizeFilterList(consultantIds).join(',') || 'none', [consultantIds]);

  // Memoize query key to prevent reference changes
  const queryKey = React.useMemo(() => {
    // Use 'loading' as placeholder when permissionsId is not yet available
    const permId = permissionsId || 'loading';
    return `${stageFilter}-${permId}-${scope}-${page}-${limit}-${stableAssignedToKey}-sdr:${stableSdrKey}-con:${stableConsultantKey}-cd:${closingDateFrom || 'none'}-${closingDateTo || 'none'}-cr:${createdFrom || 'none'}-${createdTo || 'none'}-src:${sourceFilter || 'none'}-ch:${channelFilter || 'none'}-bu:${businessUnitFilter || 'none'}`;
  }, [stageFilter, permissionsId, scope, page, limit, stableAssignedToKey, stableSdrKey, stableConsultantKey, closingDateFrom, closingDateTo, createdFrom, createdTo, sourceFilter, channelFilter, businessUnitFilter]);

  useEffect(() => {
    // Don't run if disabled or permissions are still loading
    if (!enabled || permissionsLoading) {
      logDeals('⏸️ [useDeals] Aguardando...', { enabled, permissionsLoading });
      return;
    }
    
    // CRITICAL: Don't run if permissionsId is not yet resolved
    // This prevents the first query with undefined permissionsId
    if (!permissionsId) {
      logDeals('⏸️ [useDeals] Aguardando permissionsId...');
      return;
    }
    
    // If queryKey changed, reset hasLoaded to allow new fetch
    if (lastQueryKeyRef.current !== queryKey) {
      logDeals('🔄 [useDeals] QueryKey mudou, resetando hasLoaded:', { old: lastQueryKeyRef.current, new: queryKey });
      hasLoadedRef.current = false;
    }
    
    // Skip if we've already loaded with the same query key
    if (lastQueryKeyRef.current === queryKey && hasLoadedRef.current) {
      logDeals('⏸️ [useDeals] Query já executada, pulando...', queryKey);
      return;
    }
    
    // Skip if already loading
    if (isLoadingRef.current) {
      logDeals('⏸️ [useDeals] Já carregando, pulando...', queryKey);
      return;
    }
    
    // Clear previous debounce timer
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    
    // Debounce: wait 50ms for state to stabilize before executing query
    loadTimeoutRef.current = setTimeout(() => {
      // Double-check conditions after debounce
      if (lastQueryKeyRef.current === queryKey && hasLoadedRef.current) {
        logDeals('⏸️ [useDeals] Query já executada após debounce, pulando...', queryKey);
        return;
      }
      
      logDeals('🔄 [useDeals] Executando query após debounce:', queryKey);
      lastQueryKeyRef.current = queryKey;
      hasLoadedRef.current = true;
      loadDeals();
    }, 50);
    
    // Cleanup debounce timer on unmount or dependency change
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [queryKey, enabled, permissionsLoading, permissionsId]);

  // Build query with filters applied
  const buildBaseQuery = useCallback((stage?: string) => {
    const currentScope = getScope('clients');
    
    let query = supabase
      .from('deals')
      .select(LIST_SELECT, { count: 'exact' })
      .order('created_at', { ascending: false });

    // Apply stage filter - cast to any to avoid type issues with dynamic stage values
    if (stage) {
      query = query.eq('stage', stage as any);
    }

    // Apply permissions filter
    if (currentScope === 'own_or_unassigned' && permissions?.id) {
      query = query.or(`sales_consultant.eq.${permissions.id},sales_consultant.is.null,sales_development.eq.${permissions.id},sales_development.is.null`);
    } else if (currentScope === 'own' && permissions?.id) {
      query = query.or(`sales_consultant.eq.${permissions.id},sales_development.eq.${permissions.id}`);
    }

    // Apply assignedTo filter if provided
    if (assignedTo) {
      if (assignedTo === 'unassigned') {
        query = query.is('sales_consultant', null).is('sales_development', null);
      } else if (Array.isArray(assignedTo)) {
        query = query.or(`sales_consultant.in.(${assignedTo.join(',')}),sales_development.in.(${assignedTo.join(',')})`);
      } else {
        query = query.or(`sales_consultant.eq.${assignedTo},sales_development.eq.${assignedTo}`);
      }
    }

    // SDR filter (server-side)
    if (sdrIds && sdrIds.length > 0) {
      const hasUnassigned = sdrIds.includes('unassigned');
      const realIds = sdrIds.filter(s => s !== 'unassigned');
      
      if (hasUnassigned && realIds.length > 0) {
        query = query.or(
          `sales_development.is.null,sales_development.in.(${realIds.join(',')})`
        );
      } else if (hasUnassigned) {
        query = query.is('sales_development', null);
      } else {
        query = query.in('sales_development', realIds);
      }
    }

    // Consultant filter (server-side)
    if (consultantIds && consultantIds.length > 0) {
      const hasUnassigned = consultantIds.includes('unassigned');
      const realIds = consultantIds.filter(c => c !== 'unassigned');
      
      if (hasUnassigned && realIds.length > 0) {
        query = query.or(
          `sales_consultant.is.null,sales_consultant.in.(${realIds.join(',')})`
        );
      } else if (hasUnassigned) {
        query = query.is('sales_consultant', null);
      } else {
        query = query.in('sales_consultant', realIds);
      }
    }

    // Apply closing date filter server-side (only when provided)
    // Lost deals use stage_lost_at (auto-set by DB trigger, always populated).
    // Won/other deals use closed_date (manually set).
    if (closingDateFrom || closingDateTo) {
      if (stage === 'lost') {
        if (closingDateFrom) query = query.gte('stage_lost_at', `${closingDateFrom}T00:00:00`);
        if (closingDateTo)   query = query.lte('stage_lost_at', `${closingDateTo}T23:59:59`);
      } else {
        if (closingDateFrom) query = query.gte('closed_date', closingDateFrom);
        if (closingDateTo)   query = query.lte('closed_date', closingDateTo);
      }
    }

    // Apply created_at date filter server-side
    if (createdFrom) query = query.gte('created_at', createdFrom);
    if (createdTo) query = query.lte('created_at', createdTo);

    // Apply source/channel/businessUnit filters server-side
    if (sourceFilter) query = query.eq('deals_source', sourceFilter as any);
    if (channelFilter) query = query.eq('deals_channel', channelFilter as any);
    if (businessUnitFilter) query = query.eq('business_unit', businessUnitFilter as any);

    return query;
  }, [permissions?.id, getScope, assignedTo, sdrIds, consultantIds, closingDateFrom, closingDateTo, createdFrom, createdTo, sourceFilter, channelFilter, businessUnitFilter]);

  async function loadDeals() {
    // Prevent concurrent requests
    if (isLoadingRef.current) {
      logDeals('⏸️ [useDeals] loadDeals já em execução, ignorando');
      return;
    }
    
    isLoadingRef.current = true;
    
    try {
      // Check cache first
      const cacheKey = `${stageFilter}-${permissionsId}-${scope}-${JSON.stringify(assignedTo)}-${JSON.stringify(sdrIds)}-${JSON.stringify(consultantIds)}-cd:${closingDateFrom || 'none'}-${closingDateTo || 'none'}-cr:${createdFrom || 'none'}-${createdTo || 'none'}-src:${sourceFilter || 'none'}-ch:${channelFilter || 'none'}-bu:${businessUnitFilter || 'none'}`;
      const cached = dealsCacheRef.current[cacheKey];
      
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
        logDeals('✅ [useDeals] Cache hit:', cacheKey);
        setDealsByStage(cached.dealsByStage);
        setStageCounts(cached.stageCounts);
        
        // Build flat array for legacy compatibility
        const flatDeals = Object.values(cached.dealsByStage).flat();
        setDeals(flatDeals);
        setTotalCount(Object.values(cached.stageCounts).reduce((sum, s) => sum + s.count, 0));
        
        // Set hasMore based on loaded vs total
        const hasMore: Record<string, boolean> = {};
        Object.keys(cached.dealsByStage).forEach(stage => {
          hasMore[stage] = cached.dealsByStage[stage].length < (cached.stageCounts[stage]?.count || 0);
        });
        setHasMoreByStage(hasMore);
        
        // Set server counts for legacy compatibility
        const serverCounts: Record<string, number> = {};
        Object.keys(cached.stageCounts).forEach(stage => {
          serverCounts[stage] = cached.stageCounts[stage].count;
        });
        setStageServerCounts(serverCounts);
        
        setLoading(false);
        isLoadingRef.current = false;
        return;
      }
      
      logDeals('🔄 [useDeals] Cache miss, fetching from server:', cacheKey);
      
      setLoading(true);
      setLoadingMore(false);
      setBackgroundProgress(0);

      // STEP 1: Fetch real counts from RPC (bypasses 1000 limit)
      const hasSdrOrConsultantOrDateFilter = (sdrIds && sdrIds.length > 0) || (consultantIds && consultantIds.length > 0) || createdFrom || createdTo || sourceFilter || channelFilter || businessUnitFilter;
      
      let countsData: any = null;
      let countsError: any = null;
      
      if (hasSdrOrConsultantOrDateFilter) {
        logDeals('📊 [useDeals] Fetching FILTERED stage counts via RPC...');
        const sdrRealIds = sdrIds?.filter(s => s !== 'unassigned');
        const consultantRealIds = consultantIds?.filter(c => c !== 'unassigned');
        
        const result = await supabase.rpc('get_deal_stage_counts_filtered', {
          p_sdr_ids: sdrRealIds?.length ? sdrRealIds : null,
          p_consultant_ids: consultantRealIds?.length ? consultantRealIds : null,
          p_sdr_include_unassigned: sdrIds?.includes('unassigned') || false,
          p_consultant_include_unassigned: consultantIds?.includes('unassigned') || false,
          p_source: sourceFilter || null,
          p_channel: channelFilter || null,
          p_business_unit: businessUnitFilter || null,
          p_stage_filter: stageFilter,
          p_created_from: createdFrom || null,
          p_created_to: createdTo || null
        });
        countsData = result.data;
        countsError = result.error;
      } else {
        logDeals('📊 [useDeals] Fetching stage counts via RPC...');
        const result = await supabase.rpc('get_deal_stage_counts');
        countsData = result.data;
        countsError = result.error;
      }
      
      const newStageCounts: Record<string, { count: number; amount: number }> = {};
      if (countsData && !countsError) {
        countsData.forEach((item: { stage: string; deal_count: number; total_amount: number }) => {
          newStageCounts[item.stage] = {
            count: Number(item.deal_count),
            amount: Number(item.total_amount)
          };
        });
        logDeals('📊 [useDeals] Stage counts from RPC:', newStageCounts);
      } else {
        errorDeals('❌ [useDeals] RPC error:', countsError);
      }
      setStageCounts(newStageCounts);
      
      // Build server counts for legacy compatibility
      const serverCounts: Record<string, number> = {};
      Object.keys(newStageCounts).forEach(stage => {
        serverCounts[stage] = newStageCounts[stage].count;
      });
      setStageServerCounts(serverCounts);

      // STEP 2: Determine which stages to load based on filter
      let stagesToLoad: string[] = [];
      if (stageFilter === 'active') {
        stagesToLoad = ACTIVE_STAGES;
      } else if (stageFilter === 'closed') {
        stagesToLoad = CLOSED_STAGES;
      } else {
        stagesToLoad = [...ACTIVE_STAGES, ...CLOSED_STAGES];
      }

      // STEP 3: Fetch first batch per stage in parallel
      logDeals(`⚡ [useDeals] Fetching ${CARDS_PER_STAGE} deals per stage for:`, stagesToLoad);
      
      const newDealsByStage: Record<string, DealListItem[]> = {};
      const newHasMore: Record<string, boolean> = {};
      
      // Closed/inactive deals are historical — always fetch all without pagination limit.
      // Active deals use CARDS_PER_STAGE + infinite scroll for performance.
      // Date filter also bypasses the limit to return full matching set.
      const perStageLimit = (stageFilter === 'closed' || closingDateFrom || createdFrom) ? 10000 : CARDS_PER_STAGE;

      await Promise.all(stagesToLoad.map(async (stage) => {
        const query = buildBaseQuery(stage);
        const { data, error: err } = await query.limit(perStageLimit);
        
        if (err) {
          errorDeals(`❌ [useDeals] Error loading ${stage}:`, err);
          newDealsByStage[stage] = [];
          newHasMore[stage] = false;
        } else {
          newDealsByStage[stage] = data || [];
          const stageTotal = newStageCounts[stage]?.count || 0;
          newHasMore[stage] = (data?.length || 0) < stageTotal;
          logDeals(`✅ [useDeals] ${stage}: ${data?.length || 0} loaded, ${stageTotal} total, hasMore: ${newHasMore[stage]}`);
        }
      }));

      setDealsByStage(newDealsByStage);
      setHasMoreByStage(newHasMore);
      
      // Build flat array for legacy compatibility
      const flatDeals = Object.values(newDealsByStage).flat();
      setDeals(flatDeals);
      
      // Calculate total count for relevant stages
      const relevantTotal = stagesToLoad.reduce((sum, stage) => sum + (newStageCounts[stage]?.count || 0), 0);
      setTotalCount(relevantTotal);

      // Store in cache
      dealsCacheRef.current[cacheKey] = {
        dealsByStage: newDealsByStage,
        stageCounts: newStageCounts,
        timestamp: Date.now()
      };

      logDeals('✅ [useDeals] Load complete:', {
        stages: stagesToLoad.length,
        totalLoaded: flatDeals.length,
        totalCount: relevantTotal
      });

    } catch (err) {
      errorDeals('❌ [useDeals] Erro na query:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }

  // Load more deals for a specific stage (infinite scroll trigger)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMoreForStage = useCallback(async function loadMoreForStageImpl(stage: string) {
    if (loadingMoreStage) {
      logDeals('⏸️ [useDeals] Already loading more, skipping');
      return;
    }
    
    const currentDeals = dealsByStage[stage] || [];
    const stageTotal = stageCounts[stage]?.count || 0;
    
    if (currentDeals.length >= stageTotal) {
      logDeals(`ℹ️ [useDeals] ${stage}: All deals loaded`);
      return;
    }
    
    logDeals(`📥 [useDeals] loadMoreForStage: ${stage}, offset: ${currentDeals.length}`);
    setLoadingMoreStage(stage);
    setLoadingMore(true);

    try {
      const query = buildBaseQuery(stage);
      const { data, error: err } = await query.range(currentDeals.length, currentDeals.length + LOAD_MORE_INCREMENT - 1);

      if (err) throw err;

      if (data && data.length > 0) {
        logDeals(`✅ [useDeals] loadMoreForStage: +${data.length} ${stage} deals`);
        
        // Deduplicate by ID
        const existingIds = new Set(currentDeals.map(d => d.id));
        const newDeals = data.filter(d => !existingIds.has(d.id));
        const mergedDeals = [...currentDeals, ...newDeals];
        
        setDealsByStage(prev => ({
          ...prev,
          [stage]: mergedDeals
        }));
        
        // Update hasMore
        setHasMoreByStage(prev => ({
          ...prev,
          [stage]: mergedDeals.length < stageTotal
        }));
        
        // Update flat array for legacy compatibility
        setDeals(prev => {
          const otherDeals = prev.filter(d => d.stage !== stage);
          return [...otherDeals, ...mergedDeals];
        });
        
        // Update cache
        const cacheKey = `${stageFilter}-${permissionsId}-${scope}-${JSON.stringify(assignedTo)}-${JSON.stringify(sdrIds)}-${JSON.stringify(consultantIds)}-cd:${closingDateFrom || 'none'}-${closingDateTo || 'none'}-cr:${createdFrom || 'none'}-${createdTo || 'none'}-src:${sourceFilter || 'none'}-ch:${channelFilter || 'none'}-bu:${businessUnitFilter || 'none'}`;
        if (dealsCacheRef.current[cacheKey]) {
          dealsCacheRef.current[cacheKey].dealsByStage[stage] = mergedDeals;
          dealsCacheRef.current[cacheKey].timestamp = Date.now();
        }
      } else {
        logDeals(`ℹ️ [useDeals] loadMoreForStage: No more ${stage} deals`);
        setHasMoreByStage(prev => ({
          ...prev,
          [stage]: false
        }));
      }

    } catch (err) {
      errorDeals(`❌ [useDeals] loadMoreForStage error:`, err);
    } finally {
      setLoadingMoreStage(null);
      setLoadingMore(false);
    }
  }, [loadingMoreStage, dealsByStage, stageCounts, stageFilter, permissionsId, scope, assignedTo, sdrIds, consultantIds, closingDateFrom, closingDateTo, createdFrom, createdTo, sourceFilter, channelFilter, businessUnitFilter]);

  // Invalidate all cached data (call before refetch after CRUD)
  function invalidateCache() {
    logDeals('🗑️ [useDeals] Invalidando cache');
    dealsCacheRef.current = {};
    hasLoadedRef.current = false;
    lastQueryKeyRef.current = '';
  }

  async function createDeal(deal: DealInsert) {
    // Dedupe client-side: se já existe um deal ativo recém-criado (<60s) para o mesmo contato, reaproveita.
    if (deal.contact_id) {
      const sinceIso = new Date(Date.now() - 60_000).toISOString();
      const { data: recent } = await supabase
        .from('deals')
        .select('*')
        .eq('contact_id', deal.contact_id)
        .eq('deal_status', 'active')
        .not('stage', 'in', '(won,lost)')
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (recent) {
        console.warn('[useDeals] createDeal: reaproveitando deal recém-criado', {
          contact_id: deal.contact_id,
          deal_id: recent.id,
        });
        return recent;
      }
    }

    const { data, error: err } = await supabase
      .from('deals')
      .insert(deal)
      .select()
      .single();

    if (err) {
      const msg = (err as { message?: string })?.message || '';
      if (msg.includes('DUPLICATE_DEAL_RECENT') && deal.contact_id) {
        const sinceIso = new Date(Date.now() - 60_000).toISOString();
        const { data: existing } = await supabase
          .from('deals')
          .select('*')
          .eq('contact_id', deal.contact_id)
          .eq('deal_status', 'active')
          .not('stage', 'in', '(won,lost)')
          .gte('created_at', sinceIso)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (existing) {
          console.warn('[useDeals] createDeal: trigger bloqueou duplicata, reaproveitando', {
            contact_id: deal.contact_id,
            deal_id: existing.id,
          });
          invalidateCache();
          await loadDeals();
          return existing;
        }
      }
      throw err;
    }
    invalidateCache();
    await loadDeals();
    return data;
  }

  async function updateDeal(id: string, updates: DealUpdate) {
    const { data, error: err } = await supabase
      .from('deals')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (err) throw err;
    invalidateCache();
    await loadDeals();
    return data;
  }

  async function deleteDeal(id: string) {
    const { error: err } = await supabase
      .from('deals')
      .delete()
      .eq('id', id);

    if (err) throw err;
    invalidateCache();
    await loadDeals();
  }

  // Legacy function for compatibility (used by global "load more" button)
  async function loadMoreDeals() {
    // For legacy compatibility, load more for all stages that have more
    const stagesToLoad = Object.keys(hasMoreByStage).filter(stage => hasMoreByStage[stage]);
    
    if (stagesToLoad.length === 0) {
      logDeals('ℹ️ [useDeals] loadMoreDeals: All deals loaded');
      return;
    }
    
    // Load more for the first stage that has more (prioritize active stages)
    const prioritizedStages = [...ACTIVE_STAGES, ...CLOSED_STAGES].filter(s => stagesToLoad.includes(s));
    if (prioritizedStages.length > 0) {
      await loadMoreForStage(prioritizedStages[0]);
    }
  }

  return {
    // New per-stage state
    dealsByStage,
    stageCounts,
    hasMoreByStage,
    loadingMoreStage,
    loadMoreForStage,
    
    // Legacy flat array for compatibility
    deals,
    totalCount,
    loading,
    loadingMore,
    backgroundProgress,
    error,
    stageServerCounts,
    loadDeals,
    createDeal,
    updateDeal,
    deleteDeal,
    loadMoreDeals,
    invalidateCache,
  };
}

// Fetch filtered stage counts from RPC (for when filters are active)
export async function fetchFilteredStageCounts(filters: {
  sdrIds?: string[];
  consultantIds?: string[];
  sdrIncludeUnassigned?: boolean;
  consultantIncludeUnassigned?: boolean;
  source?: string;
  channel?: string;
  businessUnit?: string;
  stageFilter?: string;
  createdFrom?: string | null;
  createdTo?: string | null;
}): Promise<Record<string, { count: number; amount: number }>> {
  logDeals('📊 [fetchFilteredStageCounts] Fetching with filters:', filters);
  
  const { data, error } = await supabase.rpc('get_deal_stage_counts_filtered', {
    p_sdr_ids: filters.sdrIds?.length ? filters.sdrIds : null,
    p_consultant_ids: filters.consultantIds?.length ? filters.consultantIds : null,
    p_sdr_include_unassigned: filters.sdrIncludeUnassigned || false,
    p_consultant_include_unassigned: filters.consultantIncludeUnassigned || false,
    p_source: filters.source && filters.source !== 'all' ? filters.source : null,
    p_channel: filters.channel && filters.channel !== 'all' ? filters.channel : null,
    p_business_unit: filters.businessUnit && filters.businessUnit !== 'all' ? filters.businessUnit : null,
    p_stage_filter: filters.stageFilter || 'active',
    p_created_from: filters.createdFrom || null,
    p_created_to: filters.createdTo || null
  });
  
  if (error) {
    errorDeals('❌ [fetchFilteredStageCounts] RPC error:', error);
    throw error;
  }
  
  const countsMap: Record<string, { count: number; amount: number }> = {};
  if (data) {
    data.forEach((item: { stage: string; deal_count: number; total_amount: number }) => {
      countsMap[item.stage] = {
        count: Number(item.deal_count),
        amount: Number(item.total_amount)
      };
    });
  }
  
  logDeals('📊 [fetchFilteredStageCounts] Result:', countsMap);
  return countsMap;
}

export function useDeal(id: string) {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) return;
    loadDeal();
  }, [id]);

  async function loadDeal() {
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('deals')
        .select('*, contact:contacts!fk_deals_contact(id, first_name, last_name, primary_email, primary_phone, primary_phone_country_code, contact_addresses:contact_addresses!contact_addresses_contact_id_fkey(*)), items(*, catalog(*))')
        .eq('id', id)
        .maybeSingle();

      if (err) {
        errorDeals("Error loading deal:", err);
        setError(err as Error);
        setDeal(null);
        return;
      }

      if (!data) {
        errorDeals("Deal not found");
        setError(new Error("Deal not found"));
        setDeal(null);
        return;
      }

      setDeal(data);
    } catch (err) {
      errorDeals("Error loading deal:", err);
      setError(err as Error);
      setDeal(null);
    } finally {
      setLoading(false);
    }
  }

  return { deal, loading, error, loadDeal };
}
